/**
 * Precomputed “Most connected” legend rows for Country Connections chrome.
 *
 * Generated by `scripts/generate-chord-most-connected.mjs` from
 * `raw/chord-connections.ts` and the optional editorial selections in
 * `data/charts/chord-most-connected-overrides.json`. Do not edit by hand —
 * re-run the script after regenerating chord connections or changing the
 * override, CHORD_MOST_CONNECTED_COUNT, or ring colour constants.
 *
 * Intentionally does **not** import the full chord dataset so client chrome
 * stays off the ~1 MiB critical path.
 */

import type {
  ChordFlywayFilter,
  ChordMostConnectedItem,
  ChordViewMode,
} from '@/content/data/charts/chord.types';

type ChordMostConnectedKey = `${ChordViewMode}--${ChordFlywayFilter}`;

export const CHORD_MOST_CONNECTED_BY_FILTER = {
  'breeding--all': [
    { id: 'RUS', label: 'Russia', color: 'hsl(7 80% 54%)', value: 197 },
    { id: 'CAN', label: 'Canada', color: 'hsl(14 80% 54%)', value: 142 },
    { id: 'USA', label: 'United States of America', color: 'hsl(73 80% 54%)', value: 127 },
    { id: 'KAZ', label: 'Kazakhstan', color: 'hsl(31 80% 54%)', value: 119 }
  ],
  'nonBreeding--all': [
    { id: 'SSD', label: 'South Sudan', color: 'hsl(203 80% 54%)', value: 79 },
    { id: 'SDN', label: 'Sudan', color: 'hsl(177 80% 54%)', value: 77 },
    { id: 'OMN', label: 'Oman', color: 'hsl(124 80% 54%)', value: 76 },
    { id: 'GIN', label: 'Guinea', color: 'hsl(190 80% 54%)', value: 66 }
  ],
  'breeding--americas': [
    { id: 'RUS', label: 'Russia', color: 'hsl(7 80% 54%)', value: 50 },
    { id: 'CAN', label: 'Canada', color: 'hsl(14 80% 54%)', value: 52 },
    { id: 'SPM', label: 'Saint Pierre and Miquelon', color: 'hsl(38 80% 54%)', value: 48 },
    { id: 'USA', label: 'United States of America', color: 'hsl(73 80% 54%)', value: 49 }
  ],
  'nonBreeding--americas': [
    { id: 'COL', label: 'Colombia', color: 'hsl(215 80% 54%)', value: 6 },
    { id: 'ECU', label: 'Ecuador', color: 'hsl(232 80% 54%)', value: 6 },
    { id: 'BRA', label: 'Brazil', color: 'hsl(249 80% 54%)', value: 9 },
    { id: 'VEN', label: 'Venezuela, Bolivarian Republic of', color: 'hsl(211 80% 54%)', value: 5 }
  ],
  'breeding--african-eurasian': [
    { id: 'RUS', label: 'Russia', color: 'hsl(7 80% 54%)', value: 117 },
    { id: 'FIN', label: 'Finland', color: 'hsl(4 80% 54%)', value: 104 },
    { id: 'NOR', label: 'Norway', color: 'hsl(8 80% 54%)', value: 108 },
    { id: 'SWE', label: 'Sweden', color: 'hsl(9 80% 54%)', value: 101 }
  ],
  'nonBreeding--african-eurasian': [
    { id: 'SSD', label: 'South Sudan', color: 'hsl(203 80% 54%)', value: 79 },
    { id: 'SDN', label: 'Sudan', color: 'hsl(177 80% 54%)', value: 77 },
    { id: 'GIN', label: 'Guinea', color: 'hsl(190 80% 54%)', value: 66 },
    { id: 'TZA', label: 'Tanzania, United Republic of', color: 'hsl(239 80% 54%)', value: 65 }
  ],
  'breeding--central-asia': [
    { id: 'RUS', label: 'Russia', color: 'hsl(7 80% 54%)', value: 28 },
    { id: 'MNG', label: 'Mongolia', color: 'hsl(39 80% 54%)', value: 26 },
    { id: 'KAZ', label: 'Kazakhstan', color: 'hsl(31 80% 54%)', value: 26 },
    { id: 'GEO', label: 'Georgia', color: 'hsl(53 80% 54%)', value: 22 }
  ],
  'nonBreeding--central-asia': [
    { id: 'OMN', label: 'Oman', color: 'hsl(124 80% 54%)', value: 18 },
    { id: 'YEM', label: 'Yemen', color: 'hsl(157 80% 54%)', value: 18 },
    { id: 'ARE', label: 'United Arab Emirates', color: 'hsl(116 80% 54%)', value: 18 },
    { id: 'MMR', label: 'Myanmar', color: 'hsl(120 80% 54%)', value: 17 }
  ],
  'breeding--east-asia-australasia': [
    { id: 'RUS', label: 'Russia', color: 'hsl(7 80% 54%)', value: 36 },
    { id: 'CAN', label: 'Canada', color: 'hsl(14 80% 54%)', value: 21 },
    { id: 'USA', label: 'United States of America', color: 'hsl(73 80% 54%)', value: 26 },
    { id: 'MNG', label: 'Mongolia', color: 'hsl(39 80% 54%)', value: 27 }
  ],
  'nonBreeding--east-asia-australasia': [
    { id: 'PLW', label: 'Palau', color: 'hsl(206 80% 54%)', value: 10 },
    { id: 'MYS', label: 'Malaysia', color: 'hsl(218 80% 54%)', value: 13 },
    { id: 'GUM', label: 'Guam', color: 'hsl(170 80% 54%)', value: 11 },
    { id: 'IDN', label: 'Indonesia', color: 'hsl(230 80% 54%)', value: 13 }
  ],
} as const satisfies Record<
  ChordMostConnectedKey,
  readonly ChordMostConnectedItem[]
>;

export function getPrecomputedMostConnected(
  flywayFilter: ChordFlywayFilter,
  viewMode: ChordViewMode,
): readonly ChordMostConnectedItem[] {
  const key = `${viewMode}--${flywayFilter}` as ChordMostConnectedKey;
  const rows = CHORD_MOST_CONNECTED_BY_FILTER[key];
  if (rows == null) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[chord-most-connected] Missing precomputed key "${key}". Re-run pnpm generate:chord-most-connected after filter/view-mode changes.`,
      );
    }
    return [];
  }
  return rows;
}
